// Basic Attributes Form Component
import React, { useEffect } from 'react';
import {
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CardContent,
    CardHeader,
    Container,
    Card,
    Box,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

const StyledContainer = styled(Container)(({ theme }) => ({
    padding: '0 !important',
    maxWidth: '1200px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: 0,
    boxShadow: 'none',
    borderBottom: 'none',
    borderTop: 'none',
    borderRadius: 0,
}));

const ViewModeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
}));

const ViewModeLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
}));

const ViewModeValue = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 500,
}));

const BasicAttributesForm = ({ preData, data, isLoading, preCreationHQPositionData, onDataChange, errors }) => {
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action') || 'view';
    const isEditable = action === 'edit';

    useEffect(() => {
        onDataChange({
            department: '',
            designation: '',
            ...(preData?.internalHqProperties?.basicAttributesData || {}),
        });
    }, [preData]);

    const departmentOptions =
        preCreationHQPositionData?.departmentsList?.map((item) => ({
            id: item.id,
            name: item.name,
        })) || [];

    const designationOptions =
        preCreationHQPositionData?.designationsList?.map((item) => ({
            id: item.id,
            name: item.name,
        })) || [];

    const handleFieldChange = (fieldName, value) => {
        onDataChange((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    return (
        <StyledContainer maxWidth="xl">
            <StyledCard>
                <CardHeader title="Basic Attributes" />
                <CardContent>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            {isEditable ? (
                                <FormControl fullWidth error={!!errors?.department} size="small">
                                    <InputLabel id="department-label">Department *</InputLabel>
                                    <Select
                                        labelId="department-label"
                                        id="department"
                                        value={data?.department || ''}
                                        label="Department *"
                                        onChange={(e) => handleFieldChange('department', e.target.value)}
                                    >
                                        {isLoading ? (
                                            <MenuItem value="">
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Loading...
                                                </Box>
                                            </MenuItem>
                                        ) : (
                                            [
                                                <MenuItem value="" key="placeholder-dept">
                                                    <em>Select Department</em>
                                                </MenuItem>,
                                                ...departmentOptions?.map((departmentOption) => (
                                                    <MenuItem key={departmentOption.id} value={departmentOption.id}>
                                                        {departmentOption.name}
                                                    </MenuItem>
                                                )),
                                            ]
                                        )}
                                    </Select>
                                    {errors?.department && <FormHelperText>{errors.department}</FormHelperText>}
                                </FormControl>
                            ) : (
                                <ViewModeContainer>
                                    <ViewModeLabel>Department</ViewModeLabel>
                                    <ViewModeValue>
                                        {departmentOptions.find((option) => option.id === data?.department)?.name ||
                                            '-'}
                                    </ViewModeValue>
                                </ViewModeContainer>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {isEditable ? (
                                <FormControl fullWidth error={!!errors?.designation} size="small">
                                    <InputLabel id="designation-label">Designation *</InputLabel>
                                    <Select
                                        labelId="designation-label"
                                        id="designation"
                                        value={data?.designation || ''}
                                        label="Designation *"
                                        onChange={(e) => handleFieldChange('designation', e.target.value)}
                                    >
                                        {isLoading ? (
                                            <MenuItem value="">
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Loading...
                                                </Box>
                                            </MenuItem>
                                        ) : (
                                            [
                                                <MenuItem value="" key="placeholder-designation">
                                                    <em>Select Designation</em>
                                                </MenuItem>,
                                                ...designationOptions?.map((designationOption) => (
                                                    <MenuItem key={designationOption.id} value={designationOption.id}>
                                                        {designationOption.name}
                                                    </MenuItem>
                                                )),
                                            ]
                                        )}
                                    </Select>
                                    {errors?.designation && <FormHelperText>{errors.designation}</FormHelperText>}
                                </FormControl>
                            ) : (
                                <ViewModeContainer>
                                    <ViewModeLabel>Designation</ViewModeLabel>
                                    <ViewModeValue>
                                        {designationOptions.find((option) => option.id === data?.designation)?.name ||
                                            '-'}
                                    </ViewModeValue>
                                </ViewModeContainer>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledCard>
        </StyledContainer>
    );
};

export default BasicAttributesForm;
